import { ReactNode, useEffect } from "react";
import {
  Anchor,
  Box,
  Container,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { Footer } from "../components/Footer";

interface LegalPageLayoutProps {
  title: string;
  titleAccent: string;
  /** Page-specific <meta name="description"> content. */
  metaDescription: string;
  lastUpdated: string;
  children: ReactNode;
}

/**
 * Legal pages are picked by a pathname switch in App.tsx rather than a router,
 * and index.html carries a single static <title>/description written for the
 * home page. Without this, /terms and /privacy would surface the home page's
 * title in the browser tab, in search results, and in link previews.
 */
function useDocumentMeta(title: string, description: string) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const existing = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    const meta = existing ?? document.createElement("meta");
    if (!existing) {
      meta.name = "description";
      document.head.appendChild(meta);
    }
    const previousDescription = meta.content;
    meta.content = description;

    return () => {
      document.title = previousTitle;
      if (existing) meta.content = previousDescription;
      else meta.remove();
    };
  }, [title, description]);
}

/**
 * Shared chrome for static legal pages (/terms, /privacy). The landing Navbar
 * is scroll-anchor driven and only makes sense on the home page, so legal
 * pages get a minimal header with a link back home instead.
 */
export function LegalPageLayout({
  title,
  titleAccent,
  metaDescription,
  lastUpdated,
  children,
}: LegalPageLayoutProps) {
  useDocumentMeta(`${title} ${titleAccent} · LivingSyncs`, metaDescription);

  // The browser's native #anchor jump fires before React renders, so deep
  // links like /privacy#hipaa land at the top without this.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  return (
    <Box style={{ minHeight: "100vh" }}>
      <Box
        component="header"
        py="md"
        style={{ borderBottom: "1px solid #e9ecef" }}
      >
        <Container size="xl">
          {/* nowrap keeps the logo and back-link on one row at 320px. */}
          <Group justify="space-between" wrap="nowrap">
            <Anchor href="/" underline="never">
              <Text
                size="xl"
                fw={700}
                style={{
                  background:
                    "linear-gradient(135deg, #4a8c71 0%, #5aba9d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                LivingSyncs
              </Text>
            </Anchor>
            <Anchor href="/" size="sm" c="dimmed" underline="never">
              <Group gap={6} wrap="nowrap">
                <IconArrowLeft size={16} />
                Back to home
              </Group>
            </Anchor>
          </Group>
        </Container>
      </Box>

      <Box component="section" py={{ base: 32, sm: 60 }}>
        <Container size="md">
          <Stack gap="md" mb={{ base: 24, sm: 40 }}>
            <Title order={1} fz={{ base: "1.75rem", sm: "2.5rem" }} fw={700}>
              {title}{" "}
              <Text
                component="span"
                inherit
                style={{
                  background:
                    "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {titleAccent}
              </Text>
            </Title>
            <Text size="sm" c="dimmed">
              Last updated: {lastUpdated}
            </Text>
          </Stack>
          <Stack gap="xl">{children}</Stack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

interface LegalSectionProps {
  heading: string;
  id?: string;
  children: ReactNode;
}

export function LegalSection({ heading, id, children }: LegalSectionProps) {
  return (
    <Stack gap="sm" id={id}>
      <Title order={2} fz={{ base: "1.1rem", sm: "1.35rem" }} fw={600}>
        {heading}
      </Title>
      {children}
    </Stack>
  );
}

interface LegalContactRowProps {
  /** Rendered as "<label>:" — the row's own label, without the colon. */
  label: string;
  children: ReactNode;
}

/**
 * One contact route per line. The contact sections used to run every route
 * together in a single paragraph, which reflowed into an unscannable block —
 * "Privacy requests:" and "Mailing address:" only read as labels when they
 * begin a line rather than landing mid-sentence after an email address.
 */
export function LegalContactRow({ label, children }: LegalContactRowProps) {
  return (
    <Text c="dimmed">
      <Text component="span" inherit fw={600}>
        {label}:
      </Text>{" "}
      {children}
    </Text>
  );
}
